import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { generateInsertValueCalcActivityData, ActivityDataModel, EquationPartsModel } from './InsertValueCalc.utils'

interface UseInsertValueCalcReturnType {
  variableMap: Map<string, string>
  equationParts: EquationPartsModel[]
  formValid: boolean
  answer: string
  handleCheckAnswer: () => void
  onInputChange: (value?: string) => void
  goToNextQuestion: () => void
  inputRef: RefObject<HTMLInputElement>
}

interface UseInsertValueCalcProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
  autofocus: boolean
  answerState: boolean | null
}

function UseInsertValueCalc({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
  autofocus,
  answerState,
}: UseInsertValueCalcProps): UseInsertValueCalcReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [answer, setAnswer] = useState<string>('')
  const [activityData, setActivityData] = useState<ActivityDataModel>({
    equationString: '',
    answer: 0,
    variableMap: new Map(),
    equationParts: [],
    maxHistoryLength: 10,
  })

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer('')
    setActivityData(generateInsertValueCalcActivityData(difficultyLevel, taskHistory))
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    onAnswerStateChange(activityData.answer.toString() === answer)
  }, [activityData.answer, answer, onAnswerStateChange])

  const onInputChange = useCallback(
    (value?: string) => {
      if (value === undefined) {
        setAnswer('')
        onAnswerStateChange(null)
      } else {
        setAnswer(value)
      }
    },
    [onAnswerStateChange],
  )

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [activityData.equationString, ...prevState].slice(0, activityData.maxHistoryLength))
    onNextQuestionClick()
  }, [onNextQuestionClick, activityData.equationString, activityData.maxHistoryLength])

  return {
    variableMap: activityData.variableMap,
    equationParts: activityData.equationParts,
    formValid: answer.length > 0,
    answer,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    inputRef,
  }
}

export default UseInsertValueCalc
